/**
 * TanStack Query hooks for `advertiser-commission`.
 * Thin wrappers over ./api — all network calls live in api.ts.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	listByAdvertiser,
	remove,
	advertiserCommissionQueryKeys,
	type AdvertiserCommissionRule,
	type CreateRuleBody,
	type ListByAdvertiserResponse,
} from "./api";

export function useAdvertiserCommissionList(
	advertiserId: string,
): UseQueryResult<ListByAdvertiserResponse, Error> {
	const id = advertiserId.trim();
	return useQuery({
		queryKey: advertiserCommissionQueryKeys.listByAdvertiser(id),
		queryFn: () => listByAdvertiser(id),
		enabled: id.length > 0,
	});
}

export function useAdvertiserCommissionCreate(): UseMutationResult<
	{ rule: AdvertiserCommissionRule },
	Error,
	CreateRuleBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserCommissionQueryKeys.all });
		},
	});
}

export function useAdvertiserCommissionDelete(): UseMutationResult<
	{ deleted: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserCommissionQueryKeys.all });
		},
	});
}
